/*
  Lunya brand adapter: the option dropdown in the sticky add-to-bag bar
  (snippets/lunya-sticky-atc-details.liquid). The source's dropdown never opens and never reaches its
  main picker; this one checks the main variant picker's own input, so Symmetry fetches the variant and
  re-renders the picker, price, URL and bar together. Delegated from document because every variant
  change replaces the bar.
*/
(() => {
  const OPEN = '.lunya-sticky-atc__toggle[aria-expanded="true"]';

  const close = (except) => {
    document.querySelectorAll(OPEN).forEach((toggle) => {
      if (toggle !== except) toggle.setAttribute('aria-expanded', 'false');
    });
  };

  // The replacement bar lands after the fetch and focus falls to <body> with the old toggle. Put it on
  // the new toggle for this request only: a focus the shopper has moved elsewhere, or a much later
  // change, is left alone. When the event fires the new toggle can still compute `visibility: hidden`
  // for a frame or more (measured ~60ms in Chromium), and focus() on it is then a silent no-op, so
  // retry each frame for up to a second until focus actually lands.
  const refocusAfterChange = (id) => {
    const expires = Date.now() + 10000;
    document.addEventListener('on:variant:change', () => {
      if (Date.now() > expires) return;
      const deadline = Date.now() + 1000;
      const attempt = () => {
        if (document.activeElement && document.activeElement !== document.body) return;
        const toggle = document.getElementById(id);
        toggle?.focus({ preventScroll: true });
        if (document.activeElement !== toggle && Date.now() < deadline) requestAnimationFrame(attempt);
      };
      attempt();
    }, { once: true });
  };

  const select = (value) => {
    const toggle = value.closest('.lunya-sticky-atc__option')?.querySelector('.lunya-sticky-atc__toggle');
    close();
    toggle?.focus({ preventScroll: true });
    if (value.getAttribute('aria-current') === 'true') return;

    const picker = value.closest('.section-main-product')?.querySelector('variant-picker');
    const valueId = CSS.escape(value.dataset.lunyaStickyValueId);
    const input = picker?.querySelector(`input.js-option[data-value-id="${valueId}"]`);
    if (input) {
      if (toggle) refocusAfterChange(toggle.id);
      input.checked = true;
      // Symmetry focuses the changed input once the variant lands unless told not to, which would
      // scroll the page back to the hero and hide the bar.
      input.dispatchEvent(new CustomEvent('change', { bubbles: true, detail: { noFocus: true } }));
      return;
    }
    // A dropdown-style option (custom-select) announces its own change; it has no noFocus path.
    const option = picker?.querySelector(`.custom-select__option[data-value-id="${valueId}"]`);
    option?.closest('custom-select')?.selectOption(option);
  };

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('.lunya-sticky-atc__toggle');
    if (toggle) {
      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      close(toggle);
      toggle.setAttribute('aria-expanded', String(!expanded));
      return;
    }
    const value = event.target.closest('.lunya-sticky-atc__value');
    if (value) {
      select(value);
      return;
    }
    close();
  });

  // Collapse an open dropdown when its bar scrolls out (assets/sticky-atc.js adds `sticky-atc--out`), so the
  // bar never comes back with it open. Every variant change replaces the bar, so observe the new one.
  const hidden = new MutationObserver((records) => {
    if (records.some(({ target }) => target.classList.contains('sticky-atc--out'))) close();
  });
  const observeBars = () => document.querySelectorAll('sticky-atc').forEach((bar) => hidden.observe(bar, { attributes: true, attributeFilter: ['class'] }));
  observeBars();
  document.addEventListener('on:variant:change', observeBars);
  document.addEventListener('shopify:section:load', observeBars);

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const open = document.querySelector(OPEN);
    if (!open) return;
    open.setAttribute('aria-expanded', 'false');
    open.focus({ preventScroll: true });
  });
})();
